import { useTranslation } from "react-i18next";
import React, { useCallback, useState, useEffect, useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { DollarSign, AlertTriangle, Info } from "lucide-react";
import { Product } from "@/types/product";
import { cn } from "@/lib/utils";
import { formatCentsAsDollars } from "@/lib/price-utils";
import { ProductThumbnail } from "@/components/products/ProductThumbnail";
import { ProductQuantityControls } from "@/components/products/ProductQuantityControls";
import { ProductDetailsDialog } from "@/components/products/ProductDetailsDialog";
import { usePcOrderScope } from "@/components/pc/PcOrderScopeProvider";

interface ProductCatalogCardProps {
  product: Product;
  quantity: number;
  onQuantityChange: (quantity: number) => void;
  showCachedPricing?: boolean;
  disabled?: boolean;
  isHighlighted?: boolean; // For pre-selected products
  isLowScore?: boolean; // For low final score (< 0.4) - use yellow with warning icon instead of blue
  isPurchased?: boolean; // For products in "Your Products"
  className?: string;
  getProductNotes?: (product: Product) => string | null | undefined;
  onCardClick?: () => void; // Handler for when the card is clicked
  hideQuantityControls?: boolean; // Hide quantity controls and add button
  showDetailsButton?: boolean; // Show an info icon (bottom-right) that opens product details
  onInfoClick?: () => void; // Optional callback fired when the info icon is pressed (e.g. analytics)
  isPriceLoading?: boolean; // Live pricing lookup in flight — show a checking indicator instead of "Call for Price"
}

export const ProductCatalogCard: React.FC<ProductCatalogCardProps> = ({
  product,
  quantity,
  onQuantityChange,
  showCachedPricing = false,
  disabled = false,
  isHighlighted = false,
  isLowScore = false,
  isPurchased = false,
  className,
  getProductNotes,
  onCardClick,
  hideQuantityControls = false,
  isPriceLoading = false,
  showDetailsButton = false,
  onInfoClick,
}) => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  // Intersection Observer for lazy loading
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      {
        rootMargin: "50px", // Start loading when card is 50px from viewport
        threshold: 0.1,
      }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const availability = showCachedPricing ? product.currentAvailability : null;
  const isOutOfStock = availability === "out_of_stock";
  const isSpecialOrder = availability === "special_order";
  const isRequestable = isOutOfStock || isSpecialOrder;
  const hasValidPrice = product.currentPrice && product.currentPrice > 0;

  // Branch availability is shown to PC staff only for now (they pick the
  // orders); the contractor view is unchanged. Null outside PC orgs.
  const isPcViewer = usePcOrderScope() !== null;

  // Format price for display (from cents)
  const formatPrice = useCallback((priceInCents: number): string => {
    return formatCentsAsDollars(priceInCents);
  }, []);

  const isUnavailable = availability === "discontinued";

  const handleCardClick = useCallback(
    (e: React.MouseEvent) => {
      // Don't trigger card click if clicking on interactive elements
      const target = e.target as HTMLElement;
      const isInteractiveElement = target.closest(
        'button, input, [role="button"]'
      );
      if (!isInteractiveElement && onCardClick) {
        onCardClick();
      }
    },
    [onCardClick]
  );

  return (
    <>
    <Card
      ref={cardRef}
      className={cn(
        // Only transition transform so the shadow/border/ring change instantly
        // (animating box-shadow alongside the press scale causes the outline to flicker).
        "relative transition-transform duration-150 flex flex-col",
        onCardClick && "cursor-pointer",
        onCardClick && "shadow-xl",
        // Desktop: subtle lift on hover.
        onCardClick && "md:hover:-translate-y-1 md:hover:border-gray-300",
        // Press feedback: a subtle scale only, to avoid border/shadow flicker.
        onCardClick && "active:scale-[0.98]",
        isHighlighted && !isLowScore && "ring-2 ring-blue-500 bg-blue-50/50",
        isHighlighted && isLowScore && "ring-2 ring-yellow-500 bg-yellow-50/50",
        quantity > 0 && "ring-2 ring-gray-600 bg-gray-50/30",
        isUnavailable && "opacity-60",
        className
      )}
      onClick={onCardClick ? handleCardClick : undefined}
    >
      <CardContent className="p-3 relative flex-1 flex flex-col">
        {/* Warning icon for low score items */}
        {isHighlighted && isLowScore && (
          <div className="absolute top-2 right-2 z-10">
            <AlertTriangle className="h-5 w-5 text-yellow-600" fill="currentColor" fillOpacity={0.2} />
          </div>
        )}
        {/* Info icon (bottom-right) — opens product details without selecting the card */}
        {showDetailsButton && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onInfoClick?.();
              setDetailsOpen(true);
            }}
            aria-label={`View details for ${product.description}`}
            className="absolute bottom-2 right-2 z-10 flex h-8 w-8 items-center justify-center rounded-full border border-gray-200/50 bg-gray-100/40 text-gray-400 backdrop-blur-sm transition-colors hover:bg-gray-200/70 hover:text-gray-600 active:scale-95 focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <Info className="h-4 w-4" />
          </button>
        )}
        {/* Mobile: Horizontal Layout (Image Left) | Desktop: Vertical Layout (Image Top) */}
        <div className="flex gap-3 items-stretch md:flex-col md:gap-0 flex-1">
          {/* Product Image and Tags Container */}
          <div className="flex flex-col md:gap-2">
            <ProductThumbnail
              imageUrl={product.imageUrl}
              alt={product.description}
              size="md"
              isVisible={isVisible}
              className={cn(
                "rounded-lg md:mx-auto",
                isRequestable && "opacity-60"
              )}
            />

            {/* Tags */}
            <div
              className={cn(
                "flex flex-col gap-1 items-start md:items-center",
                isRequestable && "opacity-60"
              )}
            >
              {isPurchased && (
                <Badge
                  variant="secondary"
                  className="text-[10px] px-1.5 mb-2 bg-blue-100 text-blue-700 hover:bg-blue-100 hover:text-blue-700"
                >
                  {t("components.products.productCatalogCard.yourProduct")}
                </Badge>
              )}
            </div>
          </div>

          {/* Content Area */}
          <div className="flex-1 flex flex-col min-w-0">
            {/* Description - Show AI description if available, otherwise regular description */}
            <div
              className={cn("mb-2 md:text-left", isRequestable && "opacity-60")}
            >
              <h4
                className="font-medium md:text-center text-left text-sm leading-tight break-words overflow-hidden"
                style={{
                  display: "-webkit-box",
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: "vertical",
                  lineHeight: "1.3em",
                }}
              >
                {product.description}
              </h4>
            </div>

            {/* Info Section */}
            <div className="flex flex-col min-w-0 flex-1">
              {/* Top section - Product ID and notes */}
              <div>
                {/* Product ID */}
                <div
                  className={cn(
                    "flex flex-wrap gap-2 mb-2 md:justify-center",
                    isRequestable && "opacity-60"
                  )}
                >
                  <span className="text-xs text-muted-foreground">
                    {t("common.id")} {product.hajocaProductId}
                  </span>
                </div>

                {/* Custom notes */}
                {getProductNotes && getProductNotes(product) && (
                  <div className="text-xs text-blue-700 bg-blue-50 px-2 py-1 rounded mb-2 break-words line-clamp-2">
                    {getProductNotes(product)}
                  </div>
                )}
              </div>

              {/* Bottom section - Price and controls, pushed to bottom */}
              <div className="mt-auto flex flex-col gap-2">
                {/* Price */}
                {showCachedPricing && (
                  <div
                    className={cn(
                      "md:text-center",
                      isRequestable && "opacity-60"
                    )}
                  >
                    {hasValidPrice ? (
                      <div className="flex items-center gap-1 md:justify-center">
                        <DollarSign className="h-3.5 w-3.5 text-green-600" />
                        <span className="font-medium text-sm text-green-600">
                          {formatPrice(product.currentPrice!)}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          / {product.unitOfMeasure}
                        </span>
                      </div>
                    ) : isPriceLoading ? (
                      // No cached price, live lookup still pending — don't
                      // declare "Call for Price" until it settles or times out
                      <span className="text-xs text-muted-foreground animate-pulse">
                        {t("components.products.productCatalogCard.checkingPrice")}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-500">
                        {t("components.products.productCatalogCard.callForPrice")}
                      </span>
                    )}
                  </div>
                )}

                {/* Branch availability (PC staff only) */}
                {isPcViewer && availability && (
                  <div className="md:text-center">
                    {availability === "in_stock" ? (
                      <span className="text-xs font-medium text-green-700">
                        {typeof product.currentQuantityAvailable === "number"
                          ? t("components.products.productCatalogCard.inStockWithQty", {
                              count: product.currentQuantityAvailable,
                            })
                          : t("components.products.productCatalogCard.inStock")}
                      </span>
                    ) : isOutOfStock ? (
                      <span className="text-xs font-medium text-amber-700">
                        {t("components.products.productCatalogCard.outOfStock")}
                      </span>
                    ) : isSpecialOrder ? (
                      <span className="text-xs font-medium text-blue-700">
                        {t("components.products.productCatalogCard.specialOrder")}
                      </span>
                    ) : (
                      <span className="text-xs font-medium text-muted-foreground">
                        {t("components.products.productCatalogCard.discontinued")}
                      </span>
                    )}
                  </div>
                )}

                {/* Quantity Controls and Add Button */}
                {!hideQuantityControls && (
                  <ProductQuantityControls
                    product={product}
                    quantity={quantity}
                    onQuantityChange={onQuantityChange}
                    showCachedPricing={showCachedPricing}
                    disabled={disabled}
                    className="md:justify-center"
                    warningClassName="md:text-center md:px-1"
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
    {showDetailsButton && (
      <ProductDetailsDialog
        product={product}
        open={detailsOpen}
        onOpenChange={setDetailsOpen}
        showCachedPricing={showCachedPricing}
      />
    )}
    </>
  );
};
